import { AbstractContentLoader } from './abstract-content-loader';
import { FormService } from '../services/form-service';
import { Logger } from '../utils/logger';
import { DomUtils } from '../utils/dom-utils';
export class ShortcodeLoader extends AbstractContentLoader {
    constructor(config) {
        super(config.ajaxUrl, config.nonce, config.postId, config.formId, config.contentId);
        this.config = config;
    }
    /**
     * Initialize the shortcode loader
     */
    initialize() {
        Logger.debug('Initializing shortcode loader', this.config);
        // Register hidden input callback
        this.registerHiddenInputCallback();
        // Set up content refresh functionality
        this.setupContentRefresh();
        // If AJAX form loading is enabled, load the form
        if (this.config.useAjaxFormLoading) {
            this.loadFormViaAjax();
        }
    }
    /**
     * Register hidden input callback for form submission
     */
    registerHiddenInputCallback() {
        FormService.registerHiddenInputCallback({
            type: 'shortcode',
            formId: this.formId,
            postId: this.postId,
            contentId: this.contentId,
            encryptedShortcode: this.config.encryptedShortcode,
        });
    }
    /**
     * Get the content container element
     */
    getContentContainer() {
        return DomUtils.getElementById(DomUtils.getContainerId('shortcode-content', this.contentId));
    }
    /**
     * Get the form container element
     */
    getFormContainer() {
        return DomUtils.getElementById(DomUtils.getFormContainerId(this.contentId));
    }
    /**
     * Get the loading container element
     */
    getLoadingContainer() {
        return DomUtils.getElementById(DomUtils.getLoadingContainerId('shortcode', this.contentId));
    }
    /**
     * Set up content refresh functionality for after form submission
     */
    setupContentRefresh() {
        // Create global function for content refresh
        if (!window.gpsaShortcodeContentLoaders) {
            window.gpsaShortcodeContentLoaders = {};
        }
        window.gpsaShortcodeContentLoaders[this.contentId] = () => {
            this.refreshContent();
        };
        Logger.debug(`Shortcode content refresh function registered for content ID: ${this.contentId}`);
    }
    /**
     * Refresh content after form submission
     */
    async refreshContent() {
        Logger.debug(`Refreshing shortcode content for content ID: ${this.contentId}`);
        // Cache element selections at the start to ensure consistency
        const contentContainer = this.getContentContainer();
        const formContainer = this.getFormContainer();
        try {
            // hide only the form as hiding the whole wrapper div causes an issue where the page is
            // scrolled to the top after form submission.
            const form = formContainer.find('form');
            DomUtils.hideElement(form);
            // DomUtils.hideElement(formContainer);
            this.showLoading(this.config.settings.content_loading_message);
            // Get updated content
            const response = await this.ajaxService.getShortcodeContent(this.postId, this.formId, this.config.encryptedShortcode);
            Logger.debug(`Shortcode content response received for content ID: ${this.contentId}`, response);
            this.hideLoading();
            if (response.has_access) {
                // User has access, show shortcode content
                Logger.debug(`User has access, displaying content for content ID: ${this.contentId}`);
                DomUtils.showElement(contentContainer, response.content);
                DomUtils.hideElement(formContainer);
            }
            else {
                // Still no access, show form again
                Logger.debug(`User does not have access, showing form for content ID: ${this.contentId}`);
                DomUtils.hideElement(contentContainer);
                DomUtils.showElement(formContainer, response.content);
            }
            // Clear the refresh function to prevent multiple calls
            window.gpsaShortcodeContentLoaders[this.contentId] = null;
        }
        catch (error) {
            this.handleAjaxError(error, 'shortcode content refresh');
        }
    }
    /**
     * Load form via AJAX (when AJAX form loading is enabled)
     */
    async loadFormViaAjax() {
        try {
            const loadingMessage = this.config.settings.ajax_form_loading_message;
            this.showLoading(loadingMessage);
            const response = await this.ajaxService.getShortcodeForm(this.formId, this.config.encryptedShortcode);
            this.hideLoading();
            DomUtils.showElement(this.getFormContainer(), response.content);
            Logger.debug('Shortcode form loaded via AJAX');
        }
        catch (error) {
            this.handleAjaxError(error, 'AJAX form loading');
        }
    }
    /**
     * Static method to create and initialize shortcode loader from config
     * @param config
     */
    static createFromConfig(config) {
        const loader = new ShortcodeLoader(config);
        loader.initialize();
        return loader;
    }
}
